import { createDebugger } from '~/scripts/utils/debug.js';
import { fetchMenuInfo, renderMenuInfo } from '~/scripts/utils/menu-info.js';

const debug = createDebugger('menu-drawer', true);

/**
 * Closes sibling <details> elements when one opens,
 * for containers marked with data-max-open-one.
 */
function initMaxOpenDetails(root) {
    root.querySelectorAll('[data-max-open-one]').forEach((container) => {
        container.querySelectorAll('details').forEach((details) => {
            details.addEventListener('toggle', () => {
                if (!details.open) return;
                const parent = details.parentElement;
                Array.from(parent.children)
                    .filter((el) => el.tagName === 'DETAILS' && el !== details)
                    .forEach((sibling) => { sibling.open = false; });
            });
        });
    });
}

document.addEventListener('DOMContentLoaded', () => initMaxOpenDetails(document));

/**
 * BrittfurnMenuDrawer
 *
 * Two-panel mobile nav drawer:
 * - Panel 1 ("main"): top-level nav items + search + footer
 * - Panel 2 (per handle): sub-items + lazy-loaded menu_info
 *
 * Toggled by any element with [data-toggle-mobile-menu].
 * Sub-panels pushed/popped via [data-menu-push] / [data-menu-back].
 */
class BrittfurnMenuDrawer extends HTMLElement {
    connectedCallback() {
        this._panel    = this.querySelector('[data-menu-panel]');
        this._backdrop = this.querySelector('[data-menu-backdrop]');
        this._isOpen   = false;
        this._currentView = 'main';
        this._viewStack   = [];
        this._fetched  = new Set();

        this._infoTemplate     = this.querySelector('[data-mobile-menu-info-template]');
        this._shortcutTemplate = this.querySelector('[data-mobile-shortcut-template]');

        debug.log('connectedCallback', {
            hasPanel:    !!this._panel,
            hasBackdrop: !!this._backdrop,
            hasInfoTemplate: !!this._infoTemplate,
            subViews: [...this.querySelectorAll('[data-menu-view]')].map(v => v.dataset.menuView),
        });

        this._resetViews();
        this._setupListeners();
        this._initSearch();
    }

    _setupListeners() {
        document.addEventListener('click', (e) => {
            if (e.target.closest('[data-toggle-mobile-menu]')) {
                debug.log('toggle click', { isOpen: this._isOpen });
                e.preventDefault();
                this._isOpen ? this.close() : this.open();
            }
        });

        this.addEventListener('click', (e) => {
            const pushBtn = e.target.closest('[data-menu-push]');
            if (pushBtn) {
                debug.log('push', { handle: pushBtn.dataset.menuPush });
                this._pushView(pushBtn.dataset.menuPush);
                return;
            }
            if (e.target.closest('[data-menu-back]')) {
                debug.log('back');
                this._popView();
            }
        });

        document.addEventListener('keyup', (e) => {
            if (e.key === 'Escape' && this._isOpen) this.close();
        });
    }

    // ----------------------------------------------------------------
    // Inline search
    // ----------------------------------------------------------------

    _initSearch() {
        const input   = this.querySelector('[data-drawer-search-input]');
        const results = this.querySelector('[data-drawer-search-results]');
        if (!input || !results) { debug.warn('_initSearch: missing input or results container'); return; }

        let timer;
        input.addEventListener('input', () => {
            clearTimeout(timer);
            const q = input.value.trim();
            if (!q) {
                results.innerHTML = '';
                results.classList.add('hidden');
                return;
            }
            timer = setTimeout(() => this._fetchSearch(q, results), 300);
        });
    }

    async _fetchSearch(q, results) {
        try {
            const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`;
            debug.log('_fetchSearch', { q, url });
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            const products = json.resources?.results?.products || [];

            if (!products.length) {
                const noResultsPrefix = results.dataset.noResultsPrefix || '';
                results.innerHTML = `<p class="px-5 py-4 text-sm text-gray-500">${noResultsPrefix} "${q}"</p>`;
                results.classList.remove('hidden');
                return;
            }

            results.innerHTML = products.map((p) => {
                const img = p.featured_image
                    ? `<img src="${p.featured_image.url}" alt="${p.title}" class="w-10 h-10 object-cover rounded shrink-0" loading="lazy">`
                    : `<span class="w-10 h-10 shrink-0 bg-gray-100 rounded"></span>`;
                return `<a href="${p.url}" class="flex items-center gap-3 px-5 py-3 border-b border-gray-100 hover:bg-gray-50">${img}<span class="text-sm">${p.title}</span></a>`;
            }).join('');
            results.classList.remove('hidden');
        } catch (err) {
            debug.error('_fetchSearch failed', { err });
        }
    }

    // ----------------------------------------------------------------
    // Panel navigation
    // ----------------------------------------------------------------

    _pushView(handle) {
        const current = this.querySelector(`[data-menu-view="${this._currentView}"]`);
        const next    = this.querySelector(`[data-menu-view="${handle}"]`);

        if (!next) { debug.warn('_pushView: no view', { handle }); return; }
        if (next === current) { debug.warn('_pushView: already on view', { handle }); return; }

        // aria-hidden pa den utglidna vyn, CSS:en gor aria-hidden-vyer
        // visibility:hidden sa en vy aldrig kan ligga kvar och "lysa igenom"
        // (spokpanel-buggen Sam sag 2026-08-12).
        if (current) { current.style.transform = 'translateX(-100%)'; current.setAttribute('aria-hidden', 'true'); }
        next.style.transform = 'translateX(0)';
        next.removeAttribute('aria-hidden');

        this._viewStack.push(this._currentView);
        this._currentView = handle;
        this._loadMenuInfo(handle);
    }

    _popView() {
        const previous = this._viewStack.pop() ?? 'main';
        const current  = this.querySelector(`[data-menu-view="${this._currentView}"]`);
        const prev     = this.querySelector(`[data-menu-view="${previous}"]`);

        if (current && current !== prev) { current.style.transform = 'translateX(100%)'; current.setAttribute('aria-hidden', 'true'); }
        if (prev) { prev.style.transform = 'translateX(0)'; prev.removeAttribute('aria-hidden'); }
        this._currentView = previous;
    }

    _resetViews() {
        this._viewStack = [];
        const main = this.querySelector('[data-menu-view="main"]');
        if (main) main.style.transform = 'translateX(0)';

        this.querySelectorAll('[data-menu-view]:not([data-menu-view="main"])').forEach((view) => {
            view.style.transform = 'translateX(100%)';
            view.setAttribute('aria-hidden', 'true');
        });
        this._currentView = 'main';
    }

    // ----------------------------------------------------------------
    // Open / close
    // ----------------------------------------------------------------

    open() {
        debug.log('open');
        this._isOpen = true;
        this.style.pointerEvents = 'auto';
        if (this._panel)    this._panel.style.transform  = 'translateX(0)';
        if (this._backdrop) this._backdrop.style.opacity = '1';
        document.body.style.overflow = 'hidden';
        this.setAttribute('open', '');
    }

    close() {
        debug.log('close');
        this._isOpen = false;
        this.style.pointerEvents = '';
        if (this._panel)    this._panel.style.transform  = 'translateX(-100%)';
        if (this._backdrop) this._backdrop.style.opacity = '0';
        document.body.style.overflow = '';
        this.removeAttribute('open');
        setTimeout(() => this._resetViews(), 300);
    }

    // ----------------------------------------------------------------
    // menu_info lazy-loading (shared logic via menu-info.js utility)
    // ----------------------------------------------------------------

    async _loadMenuInfo(handle) {
        const slot = this.querySelector(`[data-mobile-menu-info="${handle}"]`);
        if (!slot) { debug.log('_loadMenuInfo: no slot', { handle }); return; }

        const fetchUrl = slot.dataset.menuInfoUrl;
        if (!fetchUrl)              { debug.log('_loadMenuInfo: no url', { handle }); return; }
        if (this._fetched.has(handle)) { debug.log('_loadMenuInfo: cached', { handle }); return; }
        this._fetched.add(handle);

        debug.log('_loadMenuInfo: fetching', { handle, fetchUrl });

        try {
            const result = await fetchMenuInfo(fetchUrl);
            if (!result) { debug.warn('_loadMenuInfo: blank metafield', { handle }); return; }
            debug.log('_loadMenuInfo: data', { handle, data: result.data, fields: result.fields });
            renderMenuInfo(slot, this._infoTemplate, this._shortcutTemplate, result);
        } catch (err) {
            debug.error('_loadMenuInfo: failed', { handle, err });
        }
    }
}

customElements.define('brittfurn-menu-drawer', BrittfurnMenuDrawer);
